import { get, omit, set } from '@submize/lodash';
import { produce } from 'immer';
import { useSelector } from 'react-redux';
import { getForm } from '../Components/FormSetup/formSlice';
import { getFields } from '../Components/FormEditor/Editor/editorSlice';
import { getActions } from '../Components/FormActions/formActionsSlice';

const build = (fields) => fields.reduce((acc, field) => {
  acc[field.key] = {};
  const label = get(field, 'props.label.value');
  if (label) {
    set(acc[field.key], 'flags.label', label);
  }
  if (get(field, 'schema.flags.presence', '') === 'required') {
    set(acc[field.key], 'flags.presence', 'required');
  } else {
    set(acc[field.key], 'allow', ['']);
  }
  set(acc[field.key], 'type', 'string');
  if (field.type === 'Select') {
    set(acc[field.key], 'allow', field.props.options.map((o) => o.key));
  }
  if (field.type === 'Inputs') {
    set(acc[field.key], 'type', 'object');
    set(acc[field.key], 'keys', field.children.reduce((ac, item) => {
      const key = item.key.split('.')[1];
      // eslint-disable-next-line no-param-reassign
      ac[key] = { type: 'string' };
      if (get(field, 'schema.flags.presence', '') === 'required') {
        set(ac[key], 'flags.presence', 'required');
      }
      const l2 = get(item, 'props.label.value');
      if (l2) {
        set(ac[key], 'flags.label', l2);
      }
      return ac;
    }, {}));
  }
  if (field.type === 'Checkbox') {
    set(acc[field.key], 'type', 'boolean');
    if (get(field, 'props.allowIndeterminate', false)) {
      set(acc[field.key], 'allow', [null]);
    }
  }
  if (get(field, 'schema.preferences.messages.string-empty.value', false)) {
    set(acc[field.key], 'preferences.messages.string-empty', get(field, 'schema.preferences.messages.string-empty'));
  }
  return acc;
}, {});

export const buildSchema = (form, fields, actions) => {
  const newForm = produce(form, (draft) => {
    set(draft, 'schema.form.children', fields.map((f) => omit(f, ['schema'])));
    set(draft, 'schema.schema.keys', build(fields));
    set(draft, 'schema.schema.type', 'object');
    set(draft, 'schema.actions', actions);
    set(draft, 'schema.design', form.design);

    // eslint-disable-next-line no-param-reassign
    delete draft.design;

    const submit = draft.schema.form.children.find((f) => f.type === 'Submit');
    if (submit) {
      // eslint-disable-next-line no-param-reassign
      delete draft.schema.schema.keys[submit.key];
    }

    if (get(draft, 'messages.string-empty.value', false)) {
      let m = get(draft, 'messages.string-empty.value', '');
      if (get(draft, 'messages.string-empty.bold', false)) {
        m = m.replaceAll('{#label}', '<b>{#label}</b>');
      }
      set(draft.schema, 'schema.preferences.messages', { 'string.empty': m, 'any.required': m });
    }

    if (get(draft, 'messages.string-empty.quotes.enabled', false)) {
      set(draft.schema, 'schema.preferences.errors.wrap.label', get(draft, 'messages.string-empty.quotes.value', '"'));
    } else {
      set(draft.schema, 'schema.preferences.errors.wrap.label', false);
    }

    Object.values(get(draft, 'schema.schema.keys', {})).forEach((key) => {
      let text = get(key, 'preferences.messages.string-empty.value', false);
      if (text) {
        if (get(key, 'preferences.messages.string-empty.bold', false)) {
          text = text.replaceAll('{#label}', '<b>{#label}</b>');
        }
        set(key, 'preferences.messages', {
          'string.empty': text,
          'any.required': text,
        });
      }
    });
  });

  return newForm;
};

export const useSchema = () => {
  const form = useSelector(getForm);
  const fields = useSelector(getFields);
  const actions = useSelector(getActions);
  const schema = buildSchema(form, fields, actions);

  return schema;
};

export default build;
